import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Icon,
  Input,
  Portal,
  Select,
  Text,
  Textarea,
  VStack,
  createListCollection,
  Field,
  Switch,
} from "@chakra-ui/react";
import { FiChevronDown, FiChevronRight, FiTrash2 } from "react-icons/fi";
import { FaPlusCircle } from "react-icons/fa";
import { LuFileOutput, LuPlus } from "react-icons/lu";
import { useDocumentStore } from "@/stores/documents";
import { v4 as uuidv4 } from "uuid";
import useCustomToast from "@/hooks/useCustomToast";
import useAuth from "@/hooks/useAuth";
import type { UserPublic } from "@/client";

import SaveSchema from "@/components/Schema/SaveSchema";
import SchemaManagement from "@/components/Schema/SchemaManagement";
import { SchemaProvider, useSchemaContext } from "@/context/SchemaContext";

import { ExtractorService } from "@/client";

export const FIELD_TYPES = [
  "String",
  "Number",
  "Datetime",
  "Selection",
  "File",
  "Object",
  "Array",
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export interface SchemaField {
  id: string;
  name: string;
  description: string;
  type: FieldType;
  value: string | number | boolean | unknown[] | Record<string, unknown>;
  children?: SchemaField[];
  parentId?: string;
}

// 기본 JSON 스키마 구조를 위한 인터페이스
export interface JsonSchema {
  additionalProperties: boolean;
  type: "object";
  properties: Record<string, JsonSchemaField>;
  required: string[];
}

export interface JsonSchemaField {
  type: string;
  description?: string;
  properties?: Record<string, JsonSchemaField>;
  required?: string[];
  items?: JsonSchema;
  enum?: string[];
}

export interface TemplateSchema {
  name: string;
  type: string;
  description: string;
  children?: TemplateSchema[];
}

export type SerializedSchemaField = {
  name: string;
  description: string;
  type: FieldType;
  children?: SerializedSchemaField[];
};

// 사용자 정의 스키마의 고정 UUID 생성
export const USER_DEFINED_SCHEMA_ID = "00000000-0000-0000-0000-000000000000";

// UUID Regex for validation
const UUID_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// 기본 스키마를 생성하는 함수
export const createDefaultSchema = (): SchemaField[] => [
  {
    id: uuidv4(),
    name: "root",
    description: "루트 객체",
    type: "Object",
    value: {},
    children: [],
  },
];

export const DEFAULT_SCHEMA = createDefaultSchema();

export const TEMPLATE_SCHEMAS: Record<string, SchemaField[]> = {
  [USER_DEFINED_SCHEMA_ID]: createDefaultSchema(),
};

// DB 스키마를 SchemaField로 변환하는 함수
export const convertDBSchemaToSchemaField = (
  dbSchema: DBSchema
): SchemaField[] => {
  const convertField = (
    field: SerializedSchemaField | SimpleSchemaField,
    parentId?: string
  ): SchemaField => {
    // 타입 검증: FIELD_TYPES에 포함되지 않은 타입은 String으로 기본값 설정
    // 대소문자 처리: 모든 타입을 첫 글자 대문자로 변환
    let normalizedType = field.type;
    if (typeof field.type === "string") {
      if (field.type === "Object" || field.type === "Array") {
        normalizedType = field.type; // Object, Array는 그대로 유지
      } else if (field.type === "object") {
        normalizedType = "Object"; // object -> Object
      } else if (field.type === "array") {
        normalizedType = "Array"; // array -> Array
      } else {
        // 나머지는 첫 글자 대문자로 변환
        normalizedType =
          field.type.charAt(0).toUpperCase() +
          field.type.slice(1).toLowerCase();
      }
    }

    const validatedType: FieldType = FIELD_TYPES.includes(
      normalizedType as FieldType
    )
      ? (normalizedType as FieldType)
      : "String";

    let value: SchemaField["value"] = "";
    let children: SchemaField[] | undefined;

    switch (validatedType) {
      case "String":
        value = "";
        break;
      case "Number":
        value = 0;
        break;
      case "Datetime":
        value = "";
        break;
      case "Selection":
        value = false;
        break;
      case "File":
        value = "";
        break;
      case "Array":
        value = [];
        break;
      case "Object":
        value = {};
        break;
    }

    if (field.children && field.children.length > 0) {
      children = field.children.map((child) => convertField(child, field.name));
    }

    const result = {
      id: uuidv4(),
      name: field.name,
      description: field.description,
      type: validatedType,
      value,
      parentId,
      children,
    };
    return result;
  };

  // 루트 객체를 생성하고 DB 스키마의 필드들을 자식으로 추가
  const rootField: SchemaField = {
    id: uuidv4(),
    name: "root",
    description: "루트 객체",
    type: "Object",
    value: {},
    children: dbSchema.schema_content.map((field) => convertField(field)),
  };

  const result = [rootField];
  return result;
};

export const serializeSchema = (
  fields: SchemaField[]
): SerializedSchemaField[] => {
  const result: SerializedSchemaField[] = [];

  const processField = (field: SchemaField) => {
    if (field.name === "root") {
      // 루트 객체는 건너뛰고 자식들만 처리
      if (field.children) {
        field.children.forEach(processField);
      }
      return;
    }

    if (field.type === "Object" || field.type === "Array") {
      const serializedField: SerializedSchemaField = {
        name: field.name,
        description: field.description,
        type: field.type,
        children: field.children ? serializeSchema(field.children) : [],
      };
      result.push(serializedField);
    } else {
      const simpleField = {
        name: field.name,
        description: field.description,
        type: field.type,
      };
      result.push(simpleField);
    }
  };

  fields.forEach(processField);
  return result;
};

// 간단한 스키마 필드 인터페이스
export interface SimpleSchemaField {
  name: string;
  type: string;
  description: string;
  children?: SimpleSchemaField[];
}

// SchemaField를 간단한 스키마 형태로 변환하는 함수
export const convertToSimpleSchema = (
  fields: SchemaField[]
): SimpleSchemaField[] => {
  const result: SimpleSchemaField[] = [];

  const processField = (field: SchemaField) => {
    if (field.name === "root") {
      // 루트 객체는 건너뛰고 자식들만 처리
      if (field.children) {
        field.children.forEach(processField);
      }
      return;
    }

    const fieldSchema: SimpleSchemaField = {
      name: field.name,
      type:
        field.type === "Object" || field.type === "Array"
          ? field.type.toLowerCase() // Object -> object, Array -> array
          : field.type.toLowerCase(), // 나머지도 소문자로 (DB 저장용)
      description: field.description,
    };

    // Object나 Array 타입이고 자식이 있으면 children 추가
    if (
      (field.type === "Object" || field.type === "Array") &&
      field.children &&
      field.children.length > 0
    ) {
      fieldSchema.children = convertToSimpleSchema(field.children);
    }

    result.push(fieldSchema);
  };

  fields.forEach(processField);
  return result;
};

// DB에서 가져온 스키마 인터페이스
interface DBSchema {
  id: string;
  schema_name: string;
  description: string;
  schema_content: SimpleSchemaField[];
  created_at: string;
  updated_at: string;
  user_id: string;
}

interface DocumentSchemaProps {
  schema?: SchemaField[];
  onSchemaChange?: (schema: SchemaField[]) => void;
  onTemplateLoad?: (callback: () => void) => void;
  selectedTemplate?: string;
  onTemplateChange?: (templateName: string) => void;
  table_id?: string;
}

// 컴포넌트 외부에서 타입 옵션 정의
const TYPE_OPTIONS = FIELD_TYPES.map((type) => ({
  value: String(type),
  label: type,
}));

const TYPE_COLLECTION = createListCollection({ items: TYPE_OPTIONS });

const DocumentSchemaContent = ({
  schema: externalSchema,
  onSchemaChange,
  onTemplateLoad,
  onTemplateChange,
  table_id,
}: DocumentSchemaProps) => {
  const { viewMode, setViewMode, setSelectedTemplate, dbSchemas } =
    useSchemaContext();

  // 스키마 목록을 다시 가져오는 함수
  const fetchSchemas = useCallback(() => {
    // SchemaContext에서 스키마 목록을 다시 가져오는 로직이 있다면 여기서 호출
    // 현재는 빈 함수로 두고, 필요시 SchemaContext에서 제공하는 함수를 호출
  }, []);

  // 템플릿 변경 핸들러
  const handleTemplateChange = useCallback(
    (templateId: string) => {
      setSelectedTemplate(templateId);
      if (onTemplateChange) {
        onTemplateChange(templateId);
      }
    },
    [setSelectedTemplate, onTemplateChange]
  );

  // 상태 관리
  const [internalSchema, setInternalSchema] = useState<SchemaField[]>(
    createDefaultSchema()
  );
  const [collapsedFields, setCollapsedFields] = useState(new Set());
  const [internalSelectedTemplate, setInternalSelectedTemplate] =
    useState<string>(() => {
      const saved = localStorage.getItem("selectedTemplate");
      if (saved && UUID_REGEX.test(saved)) {
        return saved;
      }
      // If saved is null, undefined, or not a valid UUID,
      // clear potentially corrupted storage and use default.
      if (saved) {
        // It existed but was invalid
        localStorage.removeItem("selectedTemplate");
      }
      return USER_DEFINED_SCHEMA_ID;
    });
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const [descFocusedId, setDescFocusedId] = useState<string | null>(null);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedResult, setExtractedResult] = useState<unknown>(() => {
    const saved = localStorage.getItem("extractedResult");
    return saved ? JSON.parse(saved) : null;
  });
  const [isAdvancedExtraction, setIsAdvancedExtraction] = useState(true);

  const { pdfFile, pdfDocument, isDocumentLoaded } = useDocumentStore();
  const { user: currentUser } = useAuth();

  // onSchemaChange를 ref로 참조하여 무한 루프 방지
  const onSchemaChangeRef = useRef(onSchemaChange);
  useEffect(() => {
    onSchemaChangeRef.current = onSchemaChange;
  }, [onSchemaChange]);

  // showErrorToast와 showSuccessToast를 ref로 참조
  const showErrorToastRef = useRef(showErrorToast);
  const showSuccessToastRef = useRef(showSuccessToast);
  useEffect(() => {
    showErrorToastRef.current = showErrorToast;
    showSuccessToastRef.current = showSuccessToast;
  }, [showErrorToast, showSuccessToast]);

  // 외부 스키마가 있으면 사용, 없으면 내부 스키마 사용
  const schema = externalSchema || internalSchema;

  // Context의 selectedTemplate 변경 감지하여 스키마 로드
  const { selectedTemplate: contextSelectedTemplate } = useSchemaContext();
  useEffect(() => {
    if (
      contextSelectedTemplate &&
      contextSelectedTemplate !== internalSelectedTemplate
    ) {
      // 템플릿 스키마 로드
      if (contextSelectedTemplate === USER_DEFINED_SCHEMA_ID) {
        const defaultSchema = createDefaultSchema();
        setInternalSchema(defaultSchema);
        if (onSchemaChangeRef.current) {
          onSchemaChangeRef.current(defaultSchema);
        }
      } else {
        // DB 스키마에서 찾기
        const dbSchema = dbSchemas.find(
          (schema) => schema.id === contextSelectedTemplate
        );
        if (dbSchema) {
          const schemaFields = convertDBSchemaToSchemaField(dbSchema);
          setInternalSchema(schemaFields);
          if (onSchemaChangeRef.current) {
            onSchemaChangeRef.current(schemaFields);
          }
        } else {
        }
      }
      setInternalSelectedTemplate(contextSelectedTemplate);
    }
  }, [contextSelectedTemplate, internalSelectedTemplate, dbSchemas]);

  // 외부에서 템플릿 로드 시 스키마 자동 변경
  useEffect(() => {
    if (onTemplateLoad) {
      // onTemplateLoad prop을 통해 외부에서 호출할 수 있도록 설정
      onTemplateLoad(() => {});
    }
  }, [onTemplateLoad]);

  // 스키마 변경 시 외부 콜백 호출
  const updateSchema = useCallback((newSchema: SchemaField[]) => {
    setInternalSchema(newSchema);

    // 스키마를 즉시 로컬 스토리지에 저장 (documentSchema 키 사용)
    try {
      localStorage.setItem("documentSchema", JSON.stringify(newSchema));
    } catch (error) {
      console.error("스키마 localStorage 저장 실패:", error);
    }

    if (onSchemaChangeRef.current) {
      onSchemaChangeRef.current(newSchema);
    }
  }, []);

  // PDF가 닫힐 때 스키마와 템플릿 초기화 - 수정된 로직
  useEffect(() => {
    // PDF가 닫혔을 때만 초기화 (새로고침 시에는 초기화하지 않음)
    if (!pdfFile && !pdfDocument && !isDocumentLoaded) {
      // 추출 결과 초기화
      setExtractedResult(null);
      localStorage.removeItem("extractedResult");

      // localStorage에서 스키마가 있는지 확인
      const savedSchema = localStorage.getItem("documentSchema");

      // 저장된 스키마가 있으면 그대로 유지, 없으면 기본값 사용
      if (!savedSchema) {
        const defaultSchema = createDefaultSchema();
        setInternalSchema(defaultSchema);
        if (onSchemaChangeRef.current) {
          onSchemaChangeRef.current(defaultSchema);
        }
      }

      // 스키마 선택을 항상 사용자 정의로 초기화
      setInternalSelectedTemplate(USER_DEFINED_SCHEMA_ID);
      setSelectedTemplate(USER_DEFINED_SCHEMA_ID); // SchemaContext의 selectedTemplate도 초기화
      if (onTemplateChange) {
        onTemplateChange(USER_DEFINED_SCHEMA_ID);
      }
      localStorage.setItem("selectedTemplate", USER_DEFINED_SCHEMA_ID);
    }
  }, [
    pdfFile,
    pdfDocument,
    isDocumentLoaded,
    onTemplateChange,
    setSelectedTemplate,
  ]);

  // 샘플 스키마를 SchemaField로 변환하는 함수
  const convertSampleSchemaToSchemaField = useCallback(
    (sampleSchema: SimpleSchemaField[]): SchemaField[] => {
      const convertField = (
        field: SimpleSchemaField,
        parentId?: string
      ): SchemaField => {
        let normalizedType = field.type;
        if (typeof field.type === "string") {
          if (field.type === "Object" || field.type === "Array") {
            normalizedType = field.type; // Object, Array는 그대로 유지
          } else if (field.type === "object") {
            normalizedType = "Object"; // object -> Object
          } else if (field.type === "array") {
            normalizedType = "Array"; // array -> Array
          } else if (field.type === "string") {
            normalizedType = "String"; // string -> String
          } else if (field.type === "number") {
            normalizedType = "Number"; // number -> Number
          } else if (field.type === "datetime") {
            normalizedType = "Datetime"; // datetime -> Datetime
          } else if (field.type === "selection") {
            normalizedType = "Selection"; // selection -> Selection
          } else if (field.type === "file") {
            normalizedType = "File"; // file -> File
          } else {
            // 나머지는 첫 글자 대문자로 변환
            normalizedType =
              field.type.charAt(0).toUpperCase() +
              field.type.slice(1).toLowerCase();
          }
        }

        const validatedType: FieldType = FIELD_TYPES.includes(
          normalizedType as FieldType
        )
          ? (normalizedType as FieldType)
          : "String";

        let value: SchemaField["value"] = "";
        let children: SchemaField[] | undefined;

        switch (validatedType) {
          case "String":
            value = "";
            break;
          case "Number":
            value = 0;
            break;
          case "Datetime":
            value = "";
            break;
          case "Selection":
            value = false;
            break;
          case "File":
            value = "";
            break;
          case "Array":
            value = [];
            break;
          case "Object":
            value = {};
            break;
        }

        if (field.children && field.children.length > 0) {
          children = field.children.map((child: SimpleSchemaField) =>
            convertField(child, field.name)
          );
        }

        return {
          id: uuidv4(),
          name: field.name,
          description: field.description,
          type: validatedType,
          value,
          parentId,
          children,
        };
      };

      // 루트 객체를 생성하고 샘플 스키마의 필드들을 자식으로 추가
      const rootField: SchemaField = {
        id: uuidv4(),
        name: "root",
        description: "루트 객체",
        type: "Object",
        value: {},
        children: sampleSchema.map((field) => convertField(field)),
      };

      return [rootField];
    },
    []
  );

  // PDF가 로드될 때 샘플 스키마 확인 및 로드
  useEffect(() => {
    if (pdfFile && pdfDocument && isDocumentLoaded) {
      // 약간의 지연을 두어 localStorage 업데이트가 완료되도록 함
      const checkSampleSchema = () => {
        const sampleSchema = localStorage.getItem("sampleSchema");

        if (sampleSchema) {
          try {
            const schemaData = JSON.parse(sampleSchema);

            // 샘플 스키마를 SchemaField 형태로 변환
            const convertedSchema =
              convertSampleSchemaToSchemaField(schemaData);

            setInternalSchema(convertedSchema);
            if (onSchemaChangeRef.current) {
              onSchemaChangeRef.current(convertedSchema);
            }

            // 샘플 스키마 사용 후 localStorage에서 제거
            localStorage.removeItem("sampleSchema");

            if (showSuccessToastRef.current) {
              showSuccessToastRef.current(
                "샘플 스키마가 자동으로 로드되었습니다."
              );
            }
          } catch (error) {
            console.error("샘플 스키마 파싱 실패:", error);
          }
        } else {
        }
      };

      // 즉시 한 번 확인하고, 100ms 후에 다시 한 번 확인
      checkSampleSchema();
      setTimeout(checkSampleSchema, 100);
    }
  }, [
    pdfFile,
    pdfDocument,
    isDocumentLoaded,
    convertSampleSchemaToSchemaField,
  ]);

  const schemaJson = serializeSchema(schema);

  const filePayload = {
    name: pdfFile?.name || "document.pdf",
    data: typeof pdfFile?.data === "string" ? pdfFile.data : "", // base64 문자열
  };

  const payload = {
    schema_fields: schemaJson,
    file: filePayload,
    table_id,
  };

  // 데이터 추출 핸들러
  const handleDataExtraction = async () => {
    if (!currentUser) {
      showErrorToast("사용자 인증이 필요합니다.");
      return;
    }

    setIsExtracting(true);
    try {
      let extractedData: unknown;
      if (isAdvancedExtraction) {
        // 고급 추출(LLM)
        extractedData = await ExtractorService.mistralExtraction({
          requestBody: {
            ...payload,
            user_id: (currentUser as unknown as UserPublic).id,
          },
        });
      } else {
        // 일반 추출
        extractedData = await ExtractorService.extraction({
          requestBody: {
            ...payload,
            user_id: (currentUser as unknown as UserPublic).id,
          },
        });
      }
      setExtractedResult(extractedData);
      localStorage.setItem("extractedResult", JSON.stringify(extractedData));
      setViewMode("result");
      showSuccessToast("데이터 추출이 완료되었습니다!");
      setIsExtracting(false);
    } catch (error) {
      console.error("추출 실패:", error);
      showErrorToast("데이터 추출에 실패했습니다.");
      setIsExtracting(false);
    }
  };

  const addSiblingField = useCallback(
    (fieldId: string) => {
      const newSchema = [...schema];
      const addSibling = (fields: SchemaField[]): boolean => {
        for (let i = 0; i < fields.length; i++) {
          const f = fields[i];
          if (f.id === fieldId) {
            // 부모가 없으면 root의 children에 추가
            return false;
          }
          if (f.children) {
            const idx = f.children.findIndex((c) => c.id === fieldId);
            if (idx !== undefined && idx !== -1) {
              // 형제 위치에 새 필드 추가
              f.children.splice(idx + 1, 0, {
                id: uuidv4(),
                name: "",
                description: "",
                type: "String" as FieldType,
                value: "",
                parentId: f.id,
              });
              updateSchema(newSchema);
              return true;
            }
            if (addSibling(f.children)) return true;
          }
        }
        return false;
      };
      // 루트 children에 추가 (최상위 필드일 때)
      const root = newSchema.find((f) => f.name === "root");
      const idx = root?.children?.findIndex((c) => c.id === fieldId);
      if (idx !== undefined && idx !== -1 && root?.children) {
        root.children.splice(idx + 1, 0, {
          id: uuidv4(),
          name: "",
          description: "",
          type: "String" as FieldType,
          value: "",
          parentId: root.id,
        });
        updateSchema(newSchema);
        return;
      }
      if (addSibling(newSchema)) {
        updateSchema(newSchema);
      }
    },
    [schema, updateSchema]
  );

  const removeSchemaField = useCallback(
    (fieldId: string) => {
      const filterRecursively = (fields: SchemaField[]): SchemaField[] => {
        return fields
          .filter((f) => f.id !== fieldId)
          .map((f) => {
            if (f.children) {
              return { ...f, children: filterRecursively(f.children) };
            }
            return f;
          });
      };
      const newSchema = [...schema];
      // 루트 객체의 자식들도 처리
      const rootField = newSchema.find((f) => f.name === "root");
      if (rootField?.children) {
        const updatedRoot = {
          ...rootField,
          children: filterRecursively(rootField.children),
        };
        const rootIndex = newSchema.findIndex((f) => f.name === "root");
        if (rootIndex !== -1) {
          newSchema[rootIndex] = updatedRoot;
        }
      }
      // 일반적인 필터링도 수행
      const filteredSchema = filterRecursively(newSchema);
      updateSchema(filteredSchema);
    },
    [schema, updateSchema]
  );

  const toggleCollapse = useCallback((id: string) => {
    setCollapsedFields((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const addChildField = useCallback(
    (parentId: string) => {
      const currentSchema = schema;
      const newSchema = [...currentSchema];
      const addToField = (fields: SchemaField[]): boolean => {
        const parentIndex = fields.findIndex((f) => f.id === parentId);
        if (parentIndex !== -1) {
          const parent = fields[parentIndex];
          const currentChildren = parent.children || [];
          const newChildren = [
            ...currentChildren,
            {
              id: uuidv4(),
              name: "",
              description: "",
              type: "string" as FieldType,
              value: "",
              parentId: parentId,
            } as SchemaField,
          ];
          fields[parentIndex] = { ...parent, children: newChildren };
          return true;
        }
        for (const f of fields) {
          if (f.children && addToField(f.children)) {
            return true;
          }
        }
        return false;
      };
      // 루트 객체의 자식들도 검색 대상에 포함
      const searchInRootChildren = (): boolean => {
        const rootField = newSchema.find((f) => f.name === "root");
        if (rootField?.children) {
          return addToField(rootField.children);
        }
        return false;
      };
      if (!addToField(newSchema)) {
        searchInRootChildren();
      }
      updateSchema(newSchema);
    },
    [schema, updateSchema]
  );

  const renderField = (field: SchemaField, level = 0): JSX.Element => {
    const fieldKey = field.id;
    const isFieldNameInvalid = !field.name || field.name.trim() === "";
    return (
      <Box
        key={fieldKey}
        p={2}
        pl={level * 4 + 3}
        position="relative"
        borderRadius={0}
        boxShadow="none"
        bg="transparent"
        flex={1}
      >
        <Box
          position="relative"
          borderWidth={1}
          borderColor="gray.200"
          borderRadius="md"
          p={3}
          transition="all 0.2s"
        >
          {/* 한 줄에 정렬 */}
          <HStack gap={2} align="center" w="100%">
            {/* (1) 접기 버튼 */}
            {(field.type === "Object" || field.type === "Array") && (
              <Button
                size="xs"
                variant="ghost"
                onClick={() => toggleCollapse(fieldKey)}
                p={0}
                minW="24px"
                h="24px"
              >
                <Icon
                  as={
                    collapsedFields.has(fieldKey)
                      ? FiChevronRight
                      : FiChevronDown
                  }
                />
              </Button>
            )}
            {/* (2) 필드명 */}
            <Field.Root invalid={isFieldNameInvalid} flex={1} maxW="240px">
              <Input
                value={field.name}
                height="24px"
                py={0}
                px={2}
                fontSize="sm"
                lineHeight="24px"
                borderColor={isFieldNameInvalid ? "red.500" : undefined}
                borderRightRadius={0}
                flex={1}
                minW="120px"
                onChange={(e) => {
                  const updateFieldInSchema = (
                    fields: SchemaField[]
                  ): SchemaField[] => {
                    return fields.map((f) => {
                      if (f.id === field.id) {
                        return { ...f, name: e.target.value };
                      }
                      if (f.children) {
                        return {
                          ...f,
                          children: updateFieldInSchema(f.children),
                        };
                      }
                      return f;
                    });
                  };
                  const newSchema = updateFieldInSchema(schema);
                  updateSchema(newSchema);
                }}
                placeholder="필드명"
              />
            </Field.Root>
            {/* (3) 드롭다운 */}
            <Box minW="110px">
              <Select.Root
                collection={TYPE_COLLECTION}
                value={[String(field.type)]}
                onValueChange={(details) => {
                  const newType = details.value[0] as FieldType;
                  let newValue: SchemaField["value"] = "";
                  switch (newType) {
                    case "String":
                      newValue = "";
                      break;
                    case "Number":
                      newValue = 0;
                      break;
                    case "Datetime":
                      newValue = "";
                      break;
                    case "Selection":
                      newValue = false;
                      break;
                    case "File":
                      newValue = "";
                      break;
                    case "Array":
                      newValue = [];
                      break;
                    case "Object":
                      newValue = {};
                      break;
                  }
                  const updateFieldInSchema = (
                    fields: SchemaField[]
                  ): SchemaField[] => {
                    return fields.map((f) => {
                      if (f.id === field.id) {
                        return {
                          ...f,
                          type: newType,
                          value: newValue,
                        };
                      }
                      if (f.children) {
                        return {
                          ...f,
                          children: updateFieldInSchema(f.children),
                        };
                      }
                      return f;
                    });
                  };
                  const newSchema = updateFieldInSchema(schema);
                  updateSchema(newSchema);
                }}
                size="sm"
                width="100%"
              >
                <Select.HiddenSelect />
                <Select.Control>
                  <Select.Trigger
                    height="36px"
                    borderTopLeftRadius={0}
                    borderBottomLeftRadius={0}
                    borderLeft="none"
                  >
                    <Select.ValueText placeholder="타입 선택" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal>
                  <Select.Positioner>
                    <Select.Content>
                      {TYPE_OPTIONS.map((opt) => (
                        <Select.Item item={opt} key={opt.value}>
                          {opt.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>
            </Box>
            {/* (4) 오른쪽 버튼들: 더보기, +, 휴지통 */}
            <HStack gap={1} ml="auto">
              {(field.type === "Object" || field.type === "Array") && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (["Object", "Array"].includes(field.type)) {
                      addChildField(field.id);
                    } else {
                      addSiblingField(field.id);
                    }
                  }}
                  p={1}
                  h="24px"
                  w="24px"
                >
                  <Icon as={FaPlusCircle} boxSize={4} />
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => removeSchemaField(field.id)}
                p={1}
                h="24px"
                w="24px"
              >
                <Icon as={FiTrash2} color="red" boxSize={4} />
              </Button>
            </HStack>
          </HStack>

          {/* 필드 설명 */}
          <Textarea
            height={descFocusedId === field.id ? "120px" : "32px"}
            minH={descFocusedId === field.id ? "120px" : "32px"}
            maxH={descFocusedId === field.id ? "240px" : "32px"}
            bg="gray.50"
            borderRadius="md"
            borderColor="gray.400"
            value={field.description}
            onFocus={() => setDescFocusedId(field.id)}
            onBlur={() => setDescFocusedId(null)}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              const updateFieldInSchema = (
                fields: SchemaField[]
              ): SchemaField[] => {
                return fields.map((f) => {
                  if (f.id === field.id) {
                    return { ...f, description: e.target.value };
                  }
                  if (f.children) {
                    return { ...f, children: updateFieldInSchema(f.children) };
                  }
                  return f;
                });
              };
              const newSchema = updateFieldInSchema(schema);
              updateSchema(newSchema);
            }}
            placeholder="필드 설명"
            _placeholder={{ color: "gray.400" }}
            mt={2}
          />
        </Box>
        {(field.children?.length ?? 0) > 0 &&
          !collapsedFields.has(fieldKey) &&
          field.children?.map((child) => renderField(child, level + 1))}
      </Box>
    );
  };

  return (
    <Box h="100%" display="flex" flexDirection="column">
      <Box flexShrink={0}>
        <SchemaManagement
          currentSchema={schema}
          isExtracting={isExtracting}
          extractedResult={extractedResult}
          onDataExtraction={handleDataExtraction}
          onSchemaChange={updateSchema}
          onSaveClick={() => setIsSaveDialogOpen(true)}
          onEditSuccess={() => {
            // 스키마 변경 후 스키마 목록 갱신
            fetchSchemas();
          }}
          onDeleteSuccess={() => {
            // 삭제된 스키마가 현재 선택된 스키마였다면 기본 스키마로 초기화
            if (internalSelectedTemplate !== USER_DEFINED_SCHEMA_ID) {
              const defaultSchema = createDefaultSchema();
              setInternalSchema(defaultSchema);
              if (onSchemaChangeRef.current) {
                onSchemaChangeRef.current(defaultSchema);
              }
              setInternalSelectedTemplate(USER_DEFINED_SCHEMA_ID);
              localStorage.setItem("selectedTemplate", USER_DEFINED_SCHEMA_ID);
            }
          }}
        />
      </Box>

      <Box
        flex={1}
        minH={0}
        overflowY="auto"
        display="flex"
        flexDirection="column"
      >
        {/* 테이블 뷰일 때만 필드 편집 화면 표시 */}
        {viewMode === "table" &&
          (() => {
            const rootField = schema.find((f) => f.name === "root");
            const hasNoFields =
              !rootField ||
              !rootField.children ||
              rootField.children.length === 0;

            return (
              <Box borderWidth={1} borderRadius="md" flex={1} p={0}>
                {hasNoFields ? (
                  <Box
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    py={16}
                  >
                    <Text fontSize="2xl" fontWeight="bold" mb={2}>
                      스키마 생성
                    </Text>
                    <Text fontSize="md" color="gray.500" mb={6}>
                      문서에서 추출할 필드를 추가하세요.
                    </Text>
                  </Box>
                ) : (
                  <Box>
                    {rootField.children?.map((child) => (
                      <React.Fragment key={child.id}>
                        {renderField(child, 0)}
                      </React.Fragment>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })()}
      </Box>

      {/* 테이블 뷰일 때만 필드 추가 버튼 표시 */}
      {viewMode === "table" && (
        <Box borderTopWidth={1} p={4} bg="white">
          <VStack gap={4}>
            <HStack w="full" justify="flex-end" mb={2}>
              <Switch.Root
                checked={isAdvancedExtraction}
                onCheckedChange={(e: { checked: boolean }) =>
                  setIsAdvancedExtraction(e.checked)
                }
                colorPalette="blue"
                size="lg"
              >
                <Switch.HiddenInput />
                <Switch.Control />
                <Switch.Label>
                  {isAdvancedExtraction ? "고급" : "일반"}
                </Switch.Label>
              </Switch.Root>
            </HStack>
            <Button
              size="lg"
              w="full"
              onClick={() => {
                const rootField = schema.find((f) => f.name === "root");
                if (rootField) {
                  const newChild = {
                    id: uuidv4(),
                    name: "",
                    description: "",
                    type: "String" as const,
                    value: "",
                    parentId: rootField.id,
                  };
                  const newSchema = schema.map((f) =>
                    f.name === "root"
                      ? {
                          ...f,
                          children: [...(f.children || []), newChild],
                        }
                      : f
                  );
                  updateSchema(newSchema);
                }
              }}
            >
              <Icon as={LuPlus} mr={2} /> 필드 추가
            </Button>
            <Button
              size="lg"
              w="full"
              onClick={handleDataExtraction}
              loading={isExtracting}
              loadingText="추출 중..."
              disabled={isExtracting}
              colorScheme={isExtracting ? "gray" : "blue"}
            >
              <Icon as={LuFileOutput} mr={2} />
              {isExtracting ? "데이터 추출 중..." : "데이터 추출"}
            </Button>
          </VStack>
        </Box>
      )}

      {/* SaveSchema 다이얼로그 */}
      <SaveSchema
        isOpen={isSaveDialogOpen}
        onClose={() => setIsSaveDialogOpen(false)}
        currentSchemaContent={convertToSimpleSchema(schema)}
        onSuccess={(savedSchemaId) => {
          if (savedSchemaId) {
            handleTemplateChange(savedSchemaId);
          }
        }}
      />
    </Box>
  );
};

const DocumentSchema = (props: DocumentSchemaProps) => {
  return (
    <SchemaProvider
      onTemplateChange={props.onTemplateChange}
      onTemplateLoad={() => {
        // DocumentSchemaContent에서 템플릿 로드 로직을 처리하도록 설정
        if (props.onTemplateLoad) {
          props.onTemplateLoad(() => {});
        }
      }}
    >
      <DocumentSchemaContent {...props} />
    </SchemaProvider>
  );
};

export default DocumentSchema;
